/**
 * Authentication Provider Component
 * 
 * This component provides authentication context and state management
 * for the entire application, including user authentication, permissions,
 * and session management.
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import authAPI from '../../api/auth';
import apiClient from '../../api/client';
import { updateApiToken } from '../../api/setup';
import { User, UserPermissions } from '../../types';
import './auth.css';

// Get public user email from environment
const PUBLIC_USER_EMAIL = process.env.REACT_APP_PUBLIC_USER_EMAIL || null;

const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';

// UserPermissions is now imported from types

interface Tokens {
  accessToken: string | null;
  refreshToken: string | null;
}

interface AuthState {
  user: User | null;
  permissions: UserPermissions;
  tokens: Tokens;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  totpRequired: boolean;
  totpSessionId: string | null;
  initialized: boolean;
  permissionsOverview?: any;
  passwordResetEmail: string | null;
  passwordResetToken: string | null;
  passwordResetVerifiedToken: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string; error?: string; totp_required?: boolean }>;
  register: (userData: any) => Promise<{ success: boolean; message?: string; error?: string }>;
  verifyTotp: (email: string, totpCode: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  generateTOTP: (email: string, password?: string, tempToken?: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  forgotPassword: (email: string) => Promise<{ success: boolean; message?: string; error?: string; temp_token?: string }>;
  verifyResetCode: (email: string, totpCode: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  resetPassword: (email: string, newPassword: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  logout: () => void;
  clearError: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Authentication Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Action Types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  REGISTER_START: 'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  LOGOUT: 'LOGOUT',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  SET_USER: 'SET_USER',
  SET_PERMISSIONS: 'SET_PERMISSIONS',
  SET_PERMISSIONS_OVERVIEW: 'SET_PERMISSIONS_OVERVIEW',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  TOTP_REQUIRED: 'TOTP_REQUIRED',
  TOTP_VERIFIED: 'TOTP_VERIFIED',
  INITIALIZE: 'INITIALIZE',
  FORGOT_PASSWORD_SUCCESS: 'FORGOT_PASSWORD_SUCCESS',
  VERIFY_RESET_CODE_SUCCESS: 'VERIFY_RESET_CODE_SUCCESS',
  FORGOT_PASSWORD_CLEAR: 'FORGOT_PASSWORD_CLEAR',
} as const;

type AuthAction =
  | { type: typeof AUTH_ACTIONS.LOGIN_START }
  | { type: typeof AUTH_ACTIONS.LOGIN_SUCCESS; payload: { user: User; permissions: any; accessToken: string; refreshToken: string } }
  | { type: typeof AUTH_ACTIONS.LOGIN_FAILURE; payload: string }
  | { type: typeof AUTH_ACTIONS.REGISTER_START }
  | { type: typeof AUTH_ACTIONS.REGISTER_SUCCESS; payload: { message: string } }
  | { type: typeof AUTH_ACTIONS.REGISTER_FAILURE; payload: string }
  | { type: typeof AUTH_ACTIONS.LOGOUT }
  | { type: typeof AUTH_ACTIONS.REFRESH_TOKEN; payload: { accessToken: string; refreshToken: string } }
  | { type: typeof AUTH_ACTIONS.SET_USER; payload: User }
  | { type: typeof AUTH_ACTIONS.SET_PERMISSIONS; payload: any }
  | { type: typeof AUTH_ACTIONS.SET_PERMISSIONS_OVERVIEW; payload: any }
  | { type: typeof AUTH_ACTIONS.SET_LOADING; payload: boolean }
  | { type: typeof AUTH_ACTIONS.SET_ERROR; payload: string }
  | { type: typeof AUTH_ACTIONS.CLEAR_ERROR }
  | { type: typeof AUTH_ACTIONS.TOTP_REQUIRED; payload: { sessionId: string; user?: User } }
  | { type: typeof AUTH_ACTIONS.TOTP_VERIFIED; payload: { user: User; permissions: any; accessToken: string; refreshToken: string } }
  | { type: typeof AUTH_ACTIONS.INITIALIZE }
  | { type: typeof AUTH_ACTIONS.FORGOT_PASSWORD_SUCCESS; payload: { email: string; temp_token: string } }
  | { type: typeof AUTH_ACTIONS.VERIFY_RESET_CODE_SUCCESS; payload: { verified_token: string } }
  | { type: typeof AUTH_ACTIONS.FORGOT_PASSWORD_CLEAR };

// Helper function to transform permissions from backend format to frontend format
const transformPermissions = (backendPermissions: any): UserPermissions => {
  if (!backendPermissions) {
    return {
      valueChains: [],
      countries: [],
      dataPermissions: []
    };
  }
  
  return {
    valueChains: backendPermissions.value_chain_access || [],
    countries: backendPermissions.country_access || [],
    dataPermissions: backendPermissions.data_permissions || []
  };
};

// Initial State
const initialState: AuthState = {
  user: null,
  permissions: {
    valueChains: [],
    countries: [],
    dataPermissions: []
  },
  tokens: {
    accessToken: null,
    refreshToken: null
  },
  isAuthenticated: false,
  isLoading: true, // Start with loading true to prevent flash
  error: null,
  totpRequired: false,
  totpSessionId: null,
  initialized: false,
  permissionsOverview: undefined,
  passwordResetEmail: null,
  passwordResetToken: null,
  passwordResetVerifiedToken: null,
};

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.REGISTER_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        permissions: transformPermissions(action.payload.permissions),
        tokens: {
          accessToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
        },
        isAuthenticated: true,
        isLoading: false,
        error: null,
        totpRequired: false,
        totpSessionId: null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        isAuthenticated: false,
        user: null,
        permissions: initialState.permissions,
        tokens: initialState.tokens,
        totpRequired: false,
        totpSessionId: null,
      };

    case AUTH_ACTIONS.LOGOUT:
      // Clear API client token
      updateApiToken(null);
      return {
        ...initialState,
        initialized: true,
        isLoading: false, // Ensure loading is false after logout
      };

    case AUTH_ACTIONS.REFRESH_TOKEN:
      return {
        ...state,
        tokens: {
          accessToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
        },
        isAuthenticated: true, // Ensure isAuthenticated is true after refresh
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
      };

    case AUTH_ACTIONS.SET_PERMISSIONS:
      return {
        ...state,
        permissions: transformPermissions(action.payload),
      };
    case AUTH_ACTIONS.SET_PERMISSIONS_OVERVIEW:
      return {
        ...state,
        permissionsOverview: action.payload,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.TOTP_REQUIRED:
      if (SHOULD_DEBUG) console.log('AuthProvider - Setting TOTP session ID:', action.payload.sessionId);
      return {
        ...state,
        totpRequired: true,
        totpSessionId: action.payload.sessionId,
        user: action.payload.user || state.user,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.TOTP_VERIFIED:
      return {
        ...state,
        totpRequired: false,
        totpSessionId: null,
        user: action.payload.user,
        permissions: transformPermissions(action.payload.permissions),
        tokens: {
          accessToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
        },
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.INITIALIZE:
      return {
        ...state,
        initialized: true,
        isLoading: false,
      };

    case AUTH_ACTIONS.FORGOT_PASSWORD_SUCCESS:
      return {
        ...state,
        passwordResetEmail: action.payload.email,
        passwordResetToken: action.payload.temp_token,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.VERIFY_RESET_CODE_SUCCESS:
      return {
        ...state,
        passwordResetVerifiedToken: action.payload.verified_token,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.FORGOT_PASSWORD_CLEAR:
      return {
        ...state,
        passwordResetEmail: null,
        passwordResetToken: null,
        passwordResetVerifiedToken: null,
      };

    default:
      return state;
  }
}

// AuthProvider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (SHOULD_DEBUG) {
          console.log('AuthProvider - Starting initialization...');
          console.log('AuthProvider - localStorage on init:', Object.keys(localStorage));
          console.log('AuthProvider - localStorage values:', {
            accessToken: localStorage.getItem('auth_access_token') ? 'EXISTS' : 'NOT FOUND',
            refreshToken: localStorage.getItem('auth_refresh_token') ? 'EXISTS' : 'NOT FOUND',
            expiry: localStorage.getItem('auth_token_expiry') ? 'EXISTS' : 'NOT FOUND'
          });
          // Never log full tokens in any environment
          const at = localStorage.getItem('auth_access_token');
          const rt = localStorage.getItem('auth_refresh_token');
          console.log('AuthProvider - Access token preview:', at ? at.substring(0, 12) + '...' : 'none');
          console.log('AuthProvider - Refresh token preview:', rt ? rt.substring(0, 12) + '...' : 'none');
        }
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

        // First, ensure API client has the stored tokens
        let storedAccessToken = localStorage.getItem('auth_access_token');
        let storedRefreshToken = localStorage.getItem('auth_refresh_token');
        
        if (SHOULD_DEBUG) {
          console.log('AuthProvider - Stored tokens check:', {
            hasAccessToken: !!storedAccessToken,
            hasRefreshToken: !!storedRefreshToken,
            accessTokenLength: storedAccessToken?.length || 0,
            refreshTokenLength: storedRefreshToken?.length || 0
          });
        }
        
        if (storedAccessToken && storedRefreshToken) {
          if (SHOULD_DEBUG) console.log('AuthProvider - Restoring tokens to API client');
          updateApiToken(storedAccessToken);
          // Also restore refresh token to API client
          apiClient.setTokens(storedAccessToken, storedRefreshToken);
        }

        // Check if user is already authenticated
        if (SHOULD_DEBUG) console.log('AuthProvider - Checking authentication...');
        
        // Check if token is expired before attempting authentication
        let isTokenExpired = false;
        if (storedAccessToken) {
          try {
            const decoded = jwtDecode<JwtPayload>(storedAccessToken);
            const now = Date.now();
            const expiry = decoded.exp * 1000;
            isTokenExpired = now >= expiry;
            if (SHOULD_DEBUG) {
              console.log('AuthProvider - Direct token check:', {
                now: new Date(now).toISOString(),
                expiry: new Date(expiry).toISOString(),
                isTokenExpired,
                timeUntilExpiry: Math.round((expiry - now) / 1000 / 60) + ' minutes'
              });
            }
          } catch (error) {
            if (SHOULD_DEBUG) console.error('AuthProvider - Failed to decode stored token:', error);
            isTokenExpired = true; // If we can't decode, treat as expired
          }
        }
        
        // If token is expired, try to refresh it before checking authentication
        if (isTokenExpired && storedRefreshToken) {
          // Check if this is the public user
          let isPublicUser = false;
          if (PUBLIC_USER_EMAIL && storedAccessToken) {
            try {
              const decoded = jwtDecode<{ email?: string }>(storedAccessToken);
              isPublicUser = decoded.email === PUBLIC_USER_EMAIL;
            } catch (e) {
              // Ignore decode errors
            }
          }
          
          if (SHOULD_DEBUG) console.log('AuthProvider - Token expired, attempting refresh...', { isPublicUser });
          try {
            await apiClient.refreshAccessToken();
            // Update stored tokens after successful refresh
            const refreshedAccessToken = apiClient.getAccessToken();
            const refreshedRefreshToken = apiClient.getRefreshToken();
            if (refreshedAccessToken && refreshedRefreshToken) {
              storedAccessToken = refreshedAccessToken;
              storedRefreshToken = refreshedRefreshToken;
              isTokenExpired = false;
              if (SHOULD_DEBUG) console.log('AuthProvider - Token refreshed successfully');
            }
          } catch (refreshError) {
            if (SHOULD_DEBUG) console.error('AuthProvider - Token refresh failed during init:', refreshError);
            // For public user, preserve session even if refresh fails
            if (isPublicUser) {
              if (SHOULD_DEBUG) console.log('AuthProvider - Public user, preserving session despite refresh failure');
              // Continue with expired token for public user
              isTokenExpired = false; // Treat as not expired to continue initialization
            } else {
              // Clear tokens if refresh fails (non-public user)
              authAPI.clearTokens();
              apiClient.clearTokens();
              updateApiToken(null);
              dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
              return;
            }
          }
        }
        
        // For public user with expired token, still try to authenticate (backend may accept expired tokens for public user)
        let isPublicUser = false;
        if (PUBLIC_USER_EMAIL && storedAccessToken) {
          try {
            const decoded = jwtDecode<{ email?: string }>(storedAccessToken);
            isPublicUser = decoded.email === PUBLIC_USER_EMAIL;
          } catch (e) {
            // Ignore decode errors
          }
        }
        
        // Check authentication - for public user, allow even if token is expired
        const isAuth = (!isTokenExpired && authAPI.isAuthenticated()) || (isPublicUser && storedAccessToken);
        if (SHOULD_DEBUG) console.log('AuthProvider - isAuthenticated result:', isAuth, { isPublicUser, isTokenExpired });
        
        // Fallback: check localStorage directly if API client check fails
        // For public user, allow even with expired token
        const hasStoredTokens = storedAccessToken && storedRefreshToken && (!isTokenExpired || isPublicUser);
        if (SHOULD_DEBUG) console.log('AuthProvider - Fallback check - hasStoredTokens:', hasStoredTokens, { isPublicUser });
        
        if (isAuth || hasStoredTokens) {
          if (SHOULD_DEBUG) console.log('AuthProvider - User is authenticated, getting profile...');
          try {
            const result = await authAPI.getUserProfileAndPermissions();
            if (SHOULD_DEBUG) console.log('AuthProvider - Profile result:', result);
            
            if (result.success && result.data.user) {
              // SECURITY FIX: Validate token user_id matches fetched user_id
              // This prevents displaying wrong user's data if tokens are mismatched
              if (storedAccessToken) {
                try {
                  const decoded = jwtDecode<JwtPayload & { sub?: string; email?: string }>(storedAccessToken);
                  const tokenUserId = decoded.sub;
                  const tokenEmail = decoded.email;
                  const fetchedUserId = result.data.user.id.toString();
                  const fetchedEmail = result.data.user.email;
                  
                  if (tokenUserId !== fetchedUserId || tokenEmail !== fetchedEmail) {
                    console.error('AuthProvider - SECURITY: Token user mismatch during init!', {
                      tokenUserId,
                      fetchedUserId,
                      tokenEmail,
                      fetchedEmail
                    });
                    // Clear invalid tokens
                    localStorage.removeItem('auth_access_token');
                    localStorage.removeItem('auth_refresh_token');
                    apiClient.clearTokens();
                    updateApiToken(null);
                    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
                    return;
                  }
                } catch (error) {
                  console.error('AuthProvider - Failed to validate token during init:', error);
                  // Clear invalid tokens
                  localStorage.removeItem('auth_access_token');
                  localStorage.removeItem('auth_refresh_token');
                  apiClient.clearTokens();
                  updateApiToken(null);
                  dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
                  return;
                }
              }
              
              if (SHOULD_DEBUG) console.log('AuthProvider - Profile successful, restoring session');
              dispatch({
                type: AUTH_ACTIONS.LOGIN_SUCCESS,
                payload: {
                  user: result.data.user,
                  permissions: result.data.permissions,
                  accessToken: storedAccessToken || authAPI.getAccessToken() || '',
                  refreshToken: storedRefreshToken || authAPI.getRefreshToken() || '',
                },
              });
              // Load permissions overview (single aggregated call)
              try {
                const resp = await apiClient.authenticatedGet('/api/v1/auth/permissions/overview');
                const overview = await resp.json();
                dispatch({ type: AUTH_ACTIONS.SET_PERMISSIONS_OVERVIEW, payload: overview });
              } catch (e) {
                if (SHOULD_DEBUG) console.warn('AuthProvider - Failed to load permissions overview:', e);
              }
              if (SHOULD_DEBUG) console.log('AuthProvider - Session restored successfully');
            } else {
              if (SHOULD_DEBUG) console.log('AuthProvider - Profile fetch failed:', result.error);
              // Clear tokens and logout on authentication failure (unless public user)
              const storedAccessToken = localStorage.getItem('auth_access_token');
              let isPublicUser = false;
              if (PUBLIC_USER_EMAIL && storedAccessToken) {
                try {
                  const decoded = jwtDecode<{ email?: string }>(storedAccessToken);
                  isPublicUser = decoded.email === PUBLIC_USER_EMAIL;
                } catch (e) {
                  // Ignore decode errors
                }
              }
              
              if (!isPublicUser) {
                authAPI.clearTokens();
                apiClient.clearTokens();
                updateApiToken(null);
                dispatch({ type: AUTH_ACTIONS.LOGOUT });
              } else {
                if (SHOULD_DEBUG) console.log('AuthProvider - Public user, preserving session despite profile fetch failure');
                dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
              }
            }
          } catch (profileError: any) {
            if (SHOULD_DEBUG) console.error('AuthProvider - Error during profile fetch:', profileError);
            // If it's an authentication error, clear tokens and logout (unless public user)
            const isAuthError = profileError?.message?.includes('Authentication failed') || 
                                profileError?.message?.includes('401') ||
                                profileError?.message?.includes('Unauthorized');
            if (isAuthError) {
              const storedAccessToken = localStorage.getItem('auth_access_token');
              let isPublicUser = false;
              if (PUBLIC_USER_EMAIL && storedAccessToken) {
                try {
                  const decoded = jwtDecode<{ email?: string }>(storedAccessToken);
                  isPublicUser = decoded.email === PUBLIC_USER_EMAIL;
                } catch (e) {
                  // Ignore decode errors
                }
              }
              
              if (!isPublicUser) {
                if (SHOULD_DEBUG) console.log('AuthProvider - Authentication error detected, logging out');
                authAPI.clearTokens();
                apiClient.clearTokens();
                updateApiToken(null);
                dispatch({ type: AUTH_ACTIONS.LOGOUT });
              } else {
                if (SHOULD_DEBUG) console.log('AuthProvider - Public user authentication error, preserving session');
                dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
              }
            } else {
              // For other errors, just clear tokens but don't fully logout
              authAPI.clearTokens();
              dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
            }
          }
        } else {
          if (SHOULD_DEBUG) console.log('AuthProvider - User not authenticated, staying logged out');
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
      } catch (error) {
        if (SHOULD_DEBUG) console.error('Auth initialization error:', error);
        authAPI.clearTokens();
      } finally {
        if (SHOULD_DEBUG) console.log('AuthProvider - Initialization complete');
        dispatch({ type: AUTH_ACTIONS.INITIALIZE });
      }
    };

    initializeAuth();
  }, []);

  // Set up token change listener
  useEffect(() => {
    const handleTokenChange = (tokens: { accessToken: string | null; refreshToken: string | null }) => {
      if (tokens.accessToken && tokens.refreshToken) {
        dispatch({
          type: AUTH_ACTIONS.REFRESH_TOKEN,
          payload: {
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
          },
        });
      } else {
        // Check if current user is public user before logging out
        const currentUserEmail = state.user?.email;
        const isPublicUser = PUBLIC_USER_EMAIL && currentUserEmail === PUBLIC_USER_EMAIL;

        if (isPublicUser) {
          if (SHOULD_DEBUG) console.log('AuthProvider - Token change to null for public user, preserving session');
          return; // Don't logout public user
        }

        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };

    apiClient.addTokenChangeListener(handleTokenChange);

    return () => {
      apiClient.removeTokenChangeListener(handleTokenChange);
    };
  }, [state.user]);

  // Listen for token expiration events from OpenAPI client
  useEffect(() => {
    const handleTokenExpired = () => {
      // Check if current user is public user before logging out
      const PUBLIC_USER_EMAIL = process.env.REACT_APP_PUBLIC_USER_EMAIL || null;
      const currentUserEmail = state.user?.email;
      const isPublicUser = PUBLIC_USER_EMAIL && currentUserEmail === PUBLIC_USER_EMAIL;
      
      if (isPublicUser) {
        if (SHOULD_DEBUG) console.log('AuthProvider - Token expired event received for public user, preserving session');
        return; // Don't logout public user
      }
      
      if (SHOULD_DEBUG) console.log('AuthProvider - Token expired event received, logging out');
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);

    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, [state.user]);

  // Keep auth state in sync across tabs/windows
  useEffect(() => {
    const syncAuthState = () => {
      // Skip sync if we're loading (e.g., during login/TOTP flow)
      if (state.isLoading) {
        if (SHOULD_DEBUG) console.log('AuthProvider - Skipping sync during loading state');
        return;
      }

      const storedAccessToken = localStorage.getItem('auth_access_token');
      const storedRefreshToken = localStorage.getItem('auth_refresh_token');

      // If we think we're authenticated but tokens are missing in localStorage, force logout
      // localStorage is the source of truth for cross-tab synchronization
      if (state.isAuthenticated) {
        const tokensMissing = !storedAccessToken || !storedRefreshToken;
        
        if (tokensMissing) {
          // Check if current user is public user before logging out
          const currentUserEmail = state.user?.email;
          const isPublicUser = PUBLIC_USER_EMAIL && currentUserEmail === PUBLIC_USER_EMAIL;
          
          if (isPublicUser) {
            if (SHOULD_DEBUG) console.log('AuthProvider - Tokens missing for public user, preserving session');
            return; // Don't logout public user
          }
          
          if (SHOULD_DEBUG) console.log('AuthProvider - Tokens missing in localStorage, forcing logout for cross-tab sync', {
            hasStoredAccess: !!storedAccessToken,
            hasStoredRefresh: !!storedRefreshToken
          });
          
          // Clear everything to ensure clean state
          apiClient.clearTokens();
          updateApiToken(null);
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
          return;
        }

        // Sync API client with localStorage (localStorage is source of truth for cross-tab)
        const apiClientAccessToken = apiClient.getAccessToken();
        const apiClientRefreshToken = apiClient.getRefreshToken();
        
        if (storedAccessToken !== apiClientAccessToken || storedRefreshToken !== apiClientRefreshToken) {
          if (SHOULD_DEBUG) console.log('AuthProvider - Token mismatch detected, syncing API client from localStorage');
          // Update API client to match localStorage
          apiClient.setTokens(storedAccessToken, storedRefreshToken);
          updateApiToken(storedAccessToken);
        }
      } else {
        // If we're not authenticated but tokens exist in localStorage, they might be from another tab
        // Don't auto-login, but ensure API client is synced
        if (storedAccessToken && storedRefreshToken) {
          const apiClientAccessToken = apiClient.getAccessToken();
          const apiClientRefreshToken = apiClient.getRefreshToken();
          
          if (storedAccessToken !== apiClientAccessToken || storedRefreshToken !== apiClientRefreshToken) {
            if (SHOULD_DEBUG) console.log('AuthProvider - Syncing API client tokens from localStorage (not authenticated yet)');
            apiClient.setTokens(storedAccessToken, storedRefreshToken);
            updateApiToken(storedAccessToken);
          }
        }
      }
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) return;
      if (event.key === 'auth_access_token' || event.key === 'auth_refresh_token') {
        if (SHOULD_DEBUG) console.log('AuthProvider - Storage event detected, syncing auth state');
        // Use setTimeout to ensure localStorage has been updated
        setTimeout(syncAuthState, 0);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !state.isLoading) {
        if (SHOULD_DEBUG) console.log('AuthProvider - Tab became visible, checking auth state');
        syncAuthState();
      }
    };

    const handleWindowFocus = () => {
      if (!state.isLoading) {
        if (SHOULD_DEBUG) console.log('AuthProvider - Window focused, checking auth state');
        syncAuthState();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial sync - delay slightly to avoid interfering with login flow
    const initialSyncTimeout = setTimeout(() => {
      if (!state.isLoading) {
        syncAuthState();
      }
    }, 500);

    // Periodic check every 5 seconds to catch any missed updates (less frequent to avoid interference)
    const intervalId = setInterval(() => {
      if (state.isAuthenticated && !state.isLoading) {
        syncAuthState();
      }
    }, 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(initialSyncTimeout);
      clearInterval(intervalId);
    };
  }, [state.isAuthenticated, state.isLoading, state.user?.email]);

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; message?: string; error?: string; totp_required?: boolean }> => {
    if (SHOULD_DEBUG) console.log('AuthProvider - Login function called with:', { email, password: '***' });
    
    // SECURITY FIX: Clear old tokens FIRST to prevent using wrong user's tokens
    localStorage.removeItem('auth_access_token');
    localStorage.removeItem('auth_refresh_token');
    apiClient.clearTokens();
    updateApiToken(null);
    
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      const result = await authAPI.login({ email, password });
      if (SHOULD_DEBUG) console.log('AuthProvider - Login result:', result);
      if (result.success) {
        if (SHOULD_DEBUG) console.log('AuthProvider - Login successful, checking TOTP requirement:', result.data?.totp_required);
        if (result.data?.totp_required) {
          if (SHOULD_DEBUG) {
            console.log('AuthProvider - TOTP required, dispatching TOTP_REQUIRED action');
            console.log('AuthProvider - User data from login response:', result.data.user);
            console.log('AuthProvider - New temp_token from login:', result.data.temp_token);
          }
          dispatch({ 
            type: AUTH_ACTIONS.TOTP_REQUIRED, 
            payload: { 
              sessionId: result.data.temp_token || '',
              user: result.data.user
            } 
          });
          return { success: true, totp_required: true, message: result.data.message };
        } else {
          // CRITICAL FIX: Store tokens from login response BEFORE fetching profile
          // This ensures getUserProfileAndPermissions uses the correct tokens
          if (result.data?.tokens) {
            const { access_token, refresh_token } = result.data.tokens;
            if (SHOULD_DEBUG) console.log('AuthProvider - Storing tokens from login response before fetching profile');
            apiClient.setTokens(access_token, refresh_token);
            updateApiToken(access_token);
          }
          
          // NOW fetch user profile with correct tokens
          const profileResult = await authAPI.getUserProfileAndPermissions();
          if (profileResult.success) {
            // Use tokens from login response, fallback to stored tokens
            const accessToken = result.data?.tokens?.access_token || authAPI.getAccessToken() || '';
            const refreshToken = result.data?.tokens?.refresh_token || authAPI.getRefreshToken() || '';
            
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: {
                user: profileResult.data.user,
                permissions: profileResult.data.permissions,
                accessToken: accessToken,
                refreshToken: refreshToken,
              },
            });
            // Load permissions overview post-login
            try {
              const resp = await apiClient.authenticatedGet('/api/v1/auth/permissions/overview');
              const overview = await resp.json();
              dispatch({ type: AUTH_ACTIONS.SET_PERMISSIONS_OVERVIEW, payload: overview });
            } catch (e) {
              if (SHOULD_DEBUG) console.warn('AuthProvider - Failed to load permissions overview after login:', e);
            }
            return { success: true, message: result.data.message };
          } else {
            dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: profileResult.error || 'Failed to get user profile' });
            return { success: false, error: profileResult.error };
          }
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: result.error || 'Login failed' });
        return { success: false, error: result.error };
      }
    } catch (err: any) {
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: err.message });
      return { success: false, error: err.message };
    }
  }, []);

  const register = useCallback(async (userData: any): Promise<{ success: boolean; message?: string; error?: string }> => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });
    try {
      const result = await authAPI.register(userData);
      if (result.success) {
        // For registration, we don't immediately log in or fetch profile.
        // The user will be redirected to login or shown a success message.
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false }); // Stop loading after registration
        return { success: true, message: result.data.message };
      } else {
        dispatch({ type: AUTH_ACTIONS.REGISTER_FAILURE, payload: result.error || 'Registration failed' });
        return { success: false, error: result.error };
      }
    } catch (err: any) {
      dispatch({ type: AUTH_ACTIONS.REGISTER_FAILURE, payload: err.message });
      return { success: false, error: err.message };
    }
  }, []);

  const verifyTotp = useCallback(async (email: string, totpCode: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    try {
      if (SHOULD_DEBUG) console.log('AuthProvider - TOTP verification with token:', state.totpSessionId);
      const result = await authAPI.verifyTOTP({ 
        email, 
        totp_code: totpCode,
        temp_token: state.totpSessionId 
      });
      if (SHOULD_DEBUG) console.log('AuthProvider - TOTP verification result:', result);
      if (result.success) {
        // Use the data directly from TOTP verification response
        const { user, tokens, permissions } = result.data;
        
        // Update API client with the new tokens
        updateApiToken(tokens.access_token);
        
        dispatch({
          type: AUTH_ACTIONS.TOTP_VERIFIED,
          payload: {
            user: user,
            permissions: permissions,
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
          },
        });
        // Load permissions overview post-TOTP
        try {
          const resp = await apiClient.authenticatedGet('/api/v1/auth/permissions/overview');
          const overview = await resp.json();
          dispatch({ type: AUTH_ACTIONS.SET_PERMISSIONS_OVERVIEW, payload: overview });
        } catch (e) {
          if (SHOULD_DEBUG) console.warn('AuthProvider - Failed to load permissions overview after TOTP:', e);
        }
        return { success: true, message: result.data.message };
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: result.error || 'TOTP verification failed' });
        return { success: false, error: result.error };
      }
    } catch (err: any) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: err.message });
      return { success: false, error: err.message };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  }, [state.totpSessionId]);

  const logout = useCallback(() => {
    authAPI.logout();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  }, []);

  const generateTOTP = useCallback(async (email: string, password?: string, tempToken?: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    try {
      if (SHOULD_DEBUG) console.log('AuthProvider - Generating TOTP for:', email, { hasPassword: !!password, hasTempToken: !!tempToken });
      
      // Prefer temp_token over password
      const totpData: any = { email };
      if (tempToken) {
        totpData.temp_token = tempToken;
      } else if (password) {
        totpData.password = password;
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: 'Either password or temp token is required' });
        return { success: false, error: 'Either password or temp token is required' };
      }
      
      const result = await authAPI.generateTOTP(totpData);
      if (SHOULD_DEBUG) console.log('AuthProvider - TOTP generation result:', result);
      if (result.success) {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: null }); // Clear any previous errors
        return { success: true, message: result.data.message || 'TOTP code sent successfully' };
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: result.error || 'Failed to generate TOTP code' });
        return { success: false, error: result.error };
      }
    } catch (err: any) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: err.message || 'Failed to generate TOTP code' });
      return { success: false, error: err.message || 'Failed to generate TOTP code' };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  const forgotPassword = useCallback(async (email: string): Promise<{ success: boolean; message?: string; error?: string; temp_token?: string }> => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    try {
      if (SHOULD_DEBUG) console.log('AuthProvider - Initiating password reset for:', email);
      const result = await authAPI.forgotPassword({ email });
      if (SHOULD_DEBUG) console.log('AuthProvider - Forgot password result:', result);

      if (result.success && result.data) {
        if (result.data.temp_token) {
          dispatch({
            type: AUTH_ACTIONS.FORGOT_PASSWORD_SUCCESS,
            payload: { email, temp_token: result.data.temp_token }
          });
        }
        return { success: true, message: result.data.message, temp_token: result.data.temp_token };
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: result.error || 'Failed to initiate password reset' });
        return { success: false, error: result.error };
      }
    } catch (err: any) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: err.message || 'Failed to initiate password reset' });
      return { success: false, error: err.message };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  }, []);

  const verifyResetCode = useCallback(async (email: string, totpCode: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    try {
      if (SHOULD_DEBUG) console.log('AuthProvider - Verifying reset code for:', email);

      if (!state.passwordResetToken) {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: 'Password reset session expired. Please start again.' });
        return { success: false, error: 'Password reset session expired. Please start again.' };
      }

      const result = await authAPI.verifyResetCode({
        email,
        temp_token: state.passwordResetToken,
        totp_code: totpCode
      });

      if (SHOULD_DEBUG) console.log('AuthProvider - Verify reset code result:', result);

      if (result.success && result.data) {
        // Store the verified token
        if (result.data.verified_token) {
          dispatch({
            type: AUTH_ACTIONS.VERIFY_RESET_CODE_SUCCESS,
            payload: { verified_token: result.data.verified_token }
          });
        }
        return { success: true, message: result.data.message };
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: result.error || 'Invalid verification code' });
        return { success: false, error: result.error };
      }
    } catch (err: any) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: err.message || 'Failed to verify code' });
      return { success: false, error: err.message };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  }, [state.passwordResetToken]);

  const resetPassword = useCallback(async (email: string, newPassword: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    try {
      if (SHOULD_DEBUG) console.log('AuthProvider - Resetting password for:', email);

      if (!state.passwordResetVerifiedToken) {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: 'Verification not complete. Please verify your code first.' });
        return { success: false, error: 'Verification not complete. Please verify your code first.' };
      }

      const result = await authAPI.resetPassword({
        email,
        verified_token: state.passwordResetVerifiedToken,
        new_password: newPassword
      });

      if (SHOULD_DEBUG) console.log('AuthProvider - Reset password result:', result);

      if (result.success && result.data) {
        // Clear password reset state
        dispatch({ type: AUTH_ACTIONS.FORGOT_PASSWORD_CLEAR });
        return { success: true, message: result.data.message };
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: result.error || 'Failed to reset password' });
        return { success: false, error: result.error };
      }
    } catch (err: any) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: err.message || 'Failed to reset password' });
      return { success: false, error: err.message };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  }, [state.passwordResetVerifiedToken]);

  // Store tokens in localStorage when they change
  useEffect(() => {
    if (state.tokens.accessToken && state.tokens.refreshToken) {
      if (SHOULD_DEBUG) console.log('AuthProvider - Storing tokens in localStorage');
      localStorage.setItem('auth_access_token', state.tokens.accessToken);
      localStorage.setItem('auth_refresh_token', state.tokens.refreshToken);
      // Also update the API client
      updateApiToken(state.tokens.accessToken);
    } else if (!state.isAuthenticated && state.initialized) {
      if (SHOULD_DEBUG) console.log('AuthProvider - Clearing tokens from localStorage (user logged out)');
      localStorage.removeItem('auth_access_token');
      localStorage.removeItem('auth_refresh_token');
      updateApiToken(null);
    }
  }, [state.tokens.accessToken, state.tokens.refreshToken, state.isAuthenticated, state.initialized]);

  const value: AuthContextType = {
    ...state,
    login,
    register,
    verifyTotp,
    generateTOTP,
    forgotPassword,
    verifyResetCode,
    resetPassword,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
